import React, { useMemo } from 'react';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AlertComponent from '@ui/components/Alert/Alert.component';
import InputComponent from '@ui/components/Input/Input.component';
import useDictionary from '@ui/hooks/useDictionary';
import { IProps } from './IProps';
import UploadDeviceComponent from '../Add-device/Upload-device.component';

const Component: React.FC<IProps> = ({
    lang,
    imei,
    imeiError,
    type,
    typeError,
    password,
    passwordError,
    swVer,
    swVerError,
    hwVer,
    hwVerError,
    bootVer,
    bootVerError,
    serverError,
    uploadError,
    loading,
    deviceAlreadyExistError,
    onImeiChange,
    onTypeChange,
    onPasswordChange,
    onSwVerChange,
    onHwVerChange,
    onBootVerChange,
    onAddClick,
    onInputClick,
    onKeyDown,
    onTemplateDownload,
    onDevicesUpload,
    onBtnKeyDown,
    onClose,
}: IProps) => {
    const getWord = useDictionary();

    const mainAlertMessage = useMemo(() => {
        if (serverError) return 'serverError';
        else if (deviceAlreadyExistError) return 'deviceAlreadyExistInDbError';
        else if (uploadError) return 'incorrectFile';
        else return '';
    }, [serverError, deviceAlreadyExistError, uploadError]);

    return (
        <Box className="add-device__wrap">
            <Box className="add-device">
                <IconButton
                    className="close-btn"
                    onClick={onClose}
                    onKeyDown={onBtnKeyDown}
                >
                    <CloseIcon className="close-btn__ico" />
                </IconButton>
                {!loading && (
                    <>
                        <Typography
                            className="add-device__title_db"
                            variant="h6"
                        >
                            {getWord('addDeviceInDb')}
                        </Typography>
                        <AlertComponent
                            severity="error"
                            variant="modal"
                            lang={lang}
                            display={!!mainAlertMessage}
                            message={mainAlertMessage}
                        />
                        <FormControl
                            className="add-device__form"
                            component="form"
                        >
                            <Box className="form-row">
                                <InputComponent
                                    label="imei"
                                    name="imei"
                                    type="text"
                                    errorMessage="imeiError"
                                    lang={lang}
                                    value={imei}
                                    error={imeiError}
                                    onChange={onImeiChange}
                                    onClick={onInputClick}
                                    onFocus={onInputClick}
                                    onKeyDown={onKeyDown}
                                />
                                <InputComponent
                                    label="type"
                                    name="device-type"
                                    type="text"
                                    errorMessage="emptyTypeError"
                                    lang={lang}
                                    value={type}
                                    error={typeError}
                                    onChange={onTypeChange}
                                    onClick={onInputClick}
                                    onFocus={onInputClick}
                                    onKeyDown={onKeyDown}
                                />
                            </Box>
                            <Box className="form-row">
                                <InputComponent
                                    label="password"
                                    name="password"
                                    type="password"
                                    errorMessage="emptyPasswordError"
                                    lang={lang}
                                    value={password}
                                    error={passwordError}
                                    onChange={onPasswordChange}
                                    onClick={onInputClick}
                                    onFocus={onInputClick}
                                    onKeyDown={onKeyDown}
                                />
                                <InputComponent
                                    label="swVer"
                                    name="swVer"
                                    type="text"
                                    errorMessage="swVerError"
                                    lang={lang}
                                    value={swVer}
                                    error={swVerError}
                                    onChange={onSwVerChange}
                                    onClick={onInputClick}
                                    onFocus={onInputClick}
                                    onKeyDown={onKeyDown}
                                />
                            </Box>
                            <Box className="form-row">
                                <InputComponent
                                    label="hwVer"
                                    name="hwVer"
                                    type="text"
                                    errorMessage="hwVerError"
                                    lang={lang}
                                    value={hwVer}
                                    error={hwVerError}
                                    onChange={onHwVerChange}
                                    onClick={onInputClick}
                                    onFocus={onInputClick}
                                    onKeyDown={onKeyDown}
                                />
                                <InputComponent
                                    label="bootVer"
                                    name="bootVer"
                                    type="text"
                                    errorMessage="bootVerError"
                                    lang={lang}
                                    value={bootVer}
                                    error={bootVerError}
                                    onChange={onBootVerChange}
                                    onClick={onInputClick}
                                    onFocus={onInputClick}
                                    onKeyDown={onKeyDown}
                                />
                            </Box>
                        </FormControl>
                        <UploadDeviceComponent
                            onTemplateDownload={onTemplateDownload}
                            onDevicesUpload={onDevicesUpload}
                        />
                        <Box className="add-device__btn-bar">
                            <Button
                                className="add-device__btn"
                                variant="contained"
                                onClick={onAddClick}
                                onKeyDown={onBtnKeyDown}
                            >
                                {getWord('add')}
                            </Button>
                            <Button
                                className="add-device__btn"
                                variant="contained"
                                color="error"
                                onClick={onClose}
                                onKeyDown={onBtnKeyDown}
                            >
                                {getWord('cancel')}
                            </Button>
                        </Box>
                    </>
                )}
                {loading && (
                    <Box className="add-device__loader_db">
                        <CircularProgress size={50} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const AddDeviceInDbComponent = React.memo(Component);

export default AddDeviceInDbComponent;
